import { useState, useEffect, useRef } from 'react';
import { useModalProtection } from '@/hooks/useModalProtection';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, AlertCircle, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { z } from 'zod';
import { BulkEquipeData, EMPTY_EQUIPE } from '@/types/bulk';
import { useBulkDraft } from '@/hooks/useBulkDraft';

// Schema de validation Zod pour une ligne d'équipe
const equipeRowSchema = z.object({
  nom: z.string().min(1, 'Le nom est requis').max(100, '100 caractères max'),
  specialite: z.string().max(100, '100 caractères max').optional(),
  couleur: z.string().max(20, '20 caractères max').optional(),
});
import { useCreateEquipes } from '@/hooks/useCreateEquipes';
import { BulkEquipeRow } from './BulkEquipeRow';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface BulkEquipeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entrepriseId: string;
  onSuccess?: () => void;
  // Pour info quota (optionnel si pas de limite équipes)
  currentCount?: number;
  maxCount?: number;
}

export function BulkEquipeModal({
  open,
  onOpenChange,
  entrepriseId,
  onSuccess,
  currentCount = 0,
  maxCount,
}: BulkEquipeModalProps) {
  const { draft, setDraft, clearDraft, hasPendingDraft, restoreDraft } = useBulkDraft<BulkEquipeData>({
    key: 'bulk_equipes',
    entrepriseId,
  });

  const { createEquipes, isLoading } = useCreateEquipes();
  useModalProtection(open);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);

  // Initialiser avec une ligne vide à l'ouverture
  useEffect(() => {
    if (open) {
      if (hasPendingDraft) {
        setShowRestorePrompt(true);
      } else {
        setDraft([createEmptyRow()]);
      }
    }
  }, [open, hasPendingDraft]);

  const createEmptyRow = (): BulkEquipeData => ({
    id: uuidv4(),
    ...EMPTY_EQUIPE,
  });

  const handleRestore = () => {
    const restored = restoreDraft();
    if (!restored || restored.length === 0) {
      setDraft([createEmptyRow()]);
    }
    setShowRestorePrompt(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setDraft([createEmptyRow()]);
    setShowRestorePrompt(false);
  };

  const handleChange = (index: number, data: Partial<BulkEquipeData>) => {
    const updated = [...draft];
    updated[index] = { ...updated[index], ...data };
    setDraft(updated);

    // Clear error for this field
    if (errors[index]) {
      const newErrors = { ...errors };
      Object.keys(data).forEach(key => {
        delete newErrors[index]?.[key];
      });
      setErrors(newErrors);
    }
  };

  const handleAdd = () => {
    // Vérifier quota si applicable
    if (maxCount && currentCount + draft.length >= maxCount) {
      return;
    }
    setDraft([...draft, createEmptyRow()]);
  };

  const handleDuplicate = (index: number) => {
    // Vérifier quota si applicable
    if (maxCount && currentCount + draft.length >= maxCount) {
      return;
    }
    const duplicated: BulkEquipeData = {
      ...draft[index],
      id: uuidv4(),
      nom: '', // Vider le nom pour la copie
    };
    const updated = [...draft];
    updated.splice(index + 1, 0, duplicated);
    setDraft(updated);
  };

  const handleDelete = (index: number) => {
    if (draft.length <= 1) return;
    const updated = draft.filter((_, i) => i !== index);
    setDraft(updated);
  };

  const validate = (): boolean => {
    const newErrors: Record<number, Record<string, string>> = {};
    let isValid = true;

    draft.forEach((row, index) => {
      const result = equipeRowSchema.safeParse({
        nom: row.nom.trim(),
        specialite: row.specialite?.trim() || undefined,
        couleur: row.couleur?.trim() || undefined,
      });

      if (!result.success) {
        const rowErrors: Record<string, string> = {};
        result.error.issues.forEach(issue => {
          const field = issue.path[0] as string;
          rowErrors[field] = issue.message;
        });
        newErrors[index] = rowErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const success = await createEquipes(draft, entrepriseId);

    if (success) {
      clearDraft();
      onOpenChange(false);
      onSuccess?.();
    }
  };

  const handleClose = () => {
    // Le brouillon est déjà sauvegardé automatiquement
    onOpenChange(false);
  };

  const remainingSlots = maxCount ? maxCount - currentCount : null;
  const canAddMore = !maxCount || currentCount + draft.length < maxCount;
  const validRowsCount = draft.filter(row => row.nom.trim()).length;

  // Pulse du bouton "Ajouter une équipe" quand la 1ère équipe est remplie
  const [pulseAddBtn, setPulseAddBtn] = useState(false);
  const prevValidCount = useRef(0);
  useEffect(() => {
    if (prevValidCount.current === 0 && validRowsCount >= 1) {
      setPulseAddBtn(true);
      setTimeout(() => setPulseAddBtn(false), 3600);
    }
    prevValidCount.current = validRowsCount;
  }, [validRowsCount]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Créer des équipes</DialogTitle>
          {remainingSlots !== null && (
            <p className="text-sm text-muted-foreground">
              Équipes : {currentCount}/{maxCount} | Vous pouvez en créer {Math.max(0, remainingSlots - draft.length + validRowsCount)}
            </p>
          )}
        </DialogHeader>

        {/* Prompt de restauration de brouillon */}
        {showRestorePrompt && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Un brouillon non sauvegardé a été trouvé. Voulez-vous le restaurer ?</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleDiscardDraft}>
                  Non, recommencer
                </Button>
                <Button size="sm" onClick={handleRestore}>
                  Oui, restaurer
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Liste des équipes */}
        <div className="flex items-center gap-1.5 px-3">
          <span className="text-xs text-muted-foreground">Pictogramme & couleur</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[250px]">
                <p className="text-xs">Choisissez une icône pour reconnaître cette équipe d'un coup d'œil dans le planning et les listes</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 py-4">
          {draft.map((row, index) => (
            <BulkEquipeRow
              key={row.id}
              index={index}
              data={row}
              onChange={handleChange}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              canDelete={draft.length > 1}
              errors={errors[index] || {}}
              isFirst={index === 0}
            />
          ))}
        </div>

        {/* Bouton ajouter */}
        <Button
          type="button"
          variant="outline"
          onClick={handleAdd}
          disabled={!canAddMore}
          className={`w-full bg-white text-black font-semibold hover:bg-gray-100 hover:text-primary hover:border-primary ${pulseAddBtn ? 'animate-pulse' : ''}`}
          style={pulseAddBtn ? { animationIterationCount: 3, animationDuration: '1.2s' } : undefined}
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une équipe
        </Button>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || validRowsCount === 0}
          >
            {isLoading ? 'Création...' :
             validRowsCount === 0 ? 'Remplir au moins un nom' :
             validRowsCount === 1 ? "Créer l'équipe" :
             `Créer ${validRowsCount} équipes`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
